import { marketingInterests } from '../../data/marketing'
import { allOfferPlans as marketingPackages } from '../../data/websiteOffer'
import { trackOffer } from '../../data/offerAnalytics'
import { useState, type FormEvent } from 'react'
import { contactServices } from '../../../shared/contact'
import { TurnstileWidget } from './TurnstileWidget'
import { turnstileErrorMessage } from './turnstileConfig'

const successMessage = 'Thank you. Your message has been sent to Pack Print Mail.'
const failureMessage = 'We could not send your message. Please try again or call us at 469-756-7680.'

interface ApiResponse {
  ok?: boolean
  message?: string
  error?: string
}

type FormStatus =
  | { kind: 'idle'; message: '' }
  | { kind: 'success' | 'error'; message: string }

export function ContactForm({ marketing = false, freeWebsite = false, initialPackage = '', initialService = '' }: { marketing?: boolean; freeWebsite?: boolean; initialPackage?: string; initialService?: string }) {
  const [started, setStarted] = useState(false)
  const [status, setStatus] = useState<FormStatus>({ kind: 'idle', message: '' })
  const [turnstileToken, setTurnstileToken] = useState('')
  const [turnstileResetKey, setTurnstileResetKey] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const resetTurnstile = () => {
    setTurnstileToken('')
    setTurnstileResetKey((value) => value + 1)
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = event.currentTarget

    if (!form.reportValidity()) return
    if (!turnstileToken) {
      setStatus({ kind: 'error', message: 'Please complete the spam protection check.' })
      return
    }

    const formData = new FormData(form)
    const payload = {
      name: String(formData.get('name') ?? ''),
      email: String(formData.get('email') ?? ''),
      phone: String(formData.get('phone') ?? ''),
      subject: marketing ? `Marketing quote: ${marketingPackages.find((plan) => plan.id === formData.get('package'))?.name ?? 'Custom'}` : String(formData.get('subject') ?? ''),
      service: marketing ? 'Digital Marketing & Business Services' : String(formData.get('service') ?? ''),
      message: marketing ? [
        `Business name: ${formData.get('businessName') || 'Not supplied'}`,
        `Website / domain: ${formData.get('website') || 'Not supplied'}`,
        `Package: ${marketingPackages.find((plan) => plan.id === formData.get('package'))?.name ?? 'Custom / unsure'}`,
        ...(freeWebsite ? [`Business category: ${formData.get('category')}`, `Location: ${formData.get('location')}`, `Services offered: ${formData.get('services')}`, `Domain option: ${formData.get('domainType')}`, `Preferred domain: ${formData.get('domain')}`, `Preferred colors: ${formData.get('colors') || 'Not supplied'}`, `Marketing opt-in: ${formData.get('marketingOptIn') === 'on' ? 'Yes' : 'No'}`, 'Offer terms acknowledged. No recurring billing authorized.'] : []),
        `Services: ${formData.getAll('interests').join(', ') || 'Not selected'}`,
        `Monthly budget: ${formData.get('budget') || 'Not supplied'}`,
        `Preferred contact: ${formData.get('preferredContact') || 'Email'}`,
        '', 'Project description:', String(formData.get('message') ?? ''),
      ].join('\n') : String(formData.get('message') ?? ''),
      companyWebsite: String(formData.get('companyWebsite') ?? ''),
      turnstileToken,
    }

    setIsSubmitting(true)
    setStatus({ kind: 'idle', message: '' })

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const result = await response.json().catch(() => null) as ApiResponse | null

      if (!response.ok || !result?.ok) {
        throw new Error('CONTACT_SEND_FAILED')
      }

      form.reset()
      if (freeWebsite) trackOffer('signup_complete', 'starter')
      setStatus({ kind: 'success', message: marketing ? 'Thank you. A Pack Print Mail team member will review your request and contact you shortly.' : result.message || successMessage })
      resetTurnstile()
    } catch {
      setStatus({ kind: 'error', message: failureMessage })
      resetTurnstile()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form className={`contact-form${freeWebsite ? ' application-form' : ''}`} onSubmit={handleSubmit} onFocus={() => { if (freeWebsite && !started) { setStarted(true); trackOffer('signup_start', 'starter') } }} data-analytics-start-event={marketing ? 'custom_quote_start' : undefined} data-analytics-success-event={marketing ? 'custom_quote_submit' : undefined} noValidate>
      <div className="contact-form__intro">
        <h2>{freeWebsite ? 'Request Your Free Website' : marketing ? 'Request Your Custom Quote' : 'Send Us a Message'}</h2>
        <p>Required fields are marked with an asterisk.</p>
      </div>

      <div className="contact-form__fields">
        <div className="form-field">
          <label htmlFor="contact-name">Name <span aria-hidden="true">*</span></label>
          <input id="contact-name" name="name" type="text" autoComplete="name" maxLength={100} required />
        </div>

        <div className="form-field">
          <label htmlFor="contact-email">Email <span aria-hidden="true">*</span></label>
          <input id="contact-email" name="email" type="email" autoComplete="email" maxLength={254} required />
        </div>

        <div className="form-field">
          <label htmlFor="contact-phone">Phone{freeWebsite && ' *'}</label>
          <input id="contact-phone" name="phone" type="tel" autoComplete="tel" maxLength={40} required={freeWebsite} />
        </div>

        {!marketing && <><div className="form-field">
          <label htmlFor="contact-service">Service</label>
          <select id="contact-service" name="service" defaultValue="">
            <option value="">Select a service</option>
            {contactServices.map((service) => <option key={service} value={service}>{service}</option>)}
          </select>
        </div>

        <div className="form-field contact-form__wide">
          <label htmlFor="contact-subject">Subject</label>
          <input id="contact-subject" name="subject" type="text" maxLength={150} />
        </div>

        </>}
        {marketing && <>
          <div className="form-field"><label htmlFor="quote-business">Business Name{freeWebsite && ' *'}</label><input id="quote-business" name="businessName" autoComplete="organization" maxLength={150} required={freeWebsite} /></div>
          <div className="form-field contact-form__wide"><label htmlFor="quote-website">Website / current domain</label><input id="quote-website" name="website" autoComplete="url" placeholder="example.com" maxLength={250} /></div>
          {freeWebsite ? <input type="hidden" name="package" value="starter" /> : <div className="form-field"><label htmlFor="quote-package">Package interested in</label><select id="quote-package" name="package" defaultValue={marketingPackages.some((plan) => plan.id === initialPackage) ? initialPackage : ''}><option value="">Custom / help me choose</option>{marketingPackages.map((plan) => <option value={plan.id} key={plan.id}>{plan.name}</option>)}</select></div>}
          {freeWebsite && <><div className="form-field"><label htmlFor="website-domain-type">Domain option *</label><select id="website-domain-type" name="domainType"><option>Register a new domain</option><option>Use my existing domain</option></select></div>{[['category','Business category',100],['location','Business location',200],['services','Services offered',500],['domain','Preferred / existing domain',253],['colors','Preferred colors (optional)',100]].map(([name,label,max]) => <div className="form-field" key={name}><label htmlFor={`website-${name}`}>{label}{name !== 'colors' && ' *'}</label><input id={`website-${name}`} name={String(name)} maxLength={Number(max)} required={name !== 'colors'} /></div>)}</>}
          {!freeWebsite && <>
          <div className="form-field"><label htmlFor="quote-budget">Approximate monthly marketing budget</label><input id="quote-budget" name="budget" placeholder="For example, $500/month or unsure" maxLength={100} /></div>
          <fieldset className="marketing-interests contact-form__wide"><legend>Services interested in <span>(choose any)</span></legend><div>{marketingInterests.map((interest) => <label key={interest}><input type="checkbox" name="interests" value={interest} defaultChecked={interest === initialService} />{interest}</label>)}</div></fieldset>
          <div className="form-field contact-form__wide"><label htmlFor="quote-contact">Preferred contact method</label><select id="quote-contact" name="preferredContact" defaultValue="Email"><option>Email</option><option>Phone</option><option>No preference</option></select></div>
          </>}
        </>}
        <div className="form-field contact-form__wide">
          <label htmlFor="contact-message">{freeWebsite ? 'Short business description' : marketing ? 'Project description' : 'Message'} <span aria-hidden="true">*</span></label>
          <textarea id="contact-message" name="message" rows={freeWebsite ? 4 : 7} maxLength={freeWebsite ? 1500 : marketing ? 3000 : 5000} required />
        </div>
      </div>

      {freeWebsite && <><label className="consent-label"><input type="checkbox" name="terms" required />I understand this is an inquiry subject to eligibility review. Eligible domain registration is $15 for the first year; optional hosting after 12 months is $9.99/month or $99/year. Domain renewals are separate. This does not authorize recurring charges. *</label><label className="consent-label"><input type="checkbox" name="marketingOptIn" />Optional: send me Pack Print Mail marketing offers.</label><p className="offer-note">We use these details to respond to your inquiry. <a href="/Privacy-Policy">Privacy policy</a>.</p></>}

      <div className="contact-form__honeypot" aria-hidden="true">
        <label htmlFor="contact-company-website">Company website</label>
        <input
          id="contact-company-website"
          name="companyWebsite"
          type="text"
          autoComplete="off"
          tabIndex={-1}
          maxLength={200}
        />
      </div>

      <div className="contact-form__security">
          <TurnstileWidget
            resetKey={turnstileResetKey}
            onVerify={(token) => {
              setTurnstileToken(token)
              setStatus({ kind: 'idle', message: '' })
            }}
            onExpire={() => setTurnstileToken('')}
            onError={(code) => {
              setTurnstileToken('')
              setStatus({ kind: 'error', message: turnstileErrorMessage(code) })
            }}
          />
      </div>

      <div className="contact-form__actions">
        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Sending…' : freeWebsite ? 'Request My Free Website' : marketing ? 'Request My Custom Quote' : 'Send Message'}
        </button>
        <p
          className={`contact-form__status${status.kind === 'idle' ? '' : ` is-${status.kind}`}`}
          role={status.kind === 'error' ? 'alert' : 'status'}
          aria-live="polite"
        >
          {status.message}
        </p>
      </div>
    </form>
  )
}
